import { stalePendingCount } from './teller-nudge';

import { type Referral } from './referrals-store';

/**
 * SCRUM-93 — Teller Nudge Banner. Alerts a Teller when their own submitted
 * referrals have been sitting Pending for too long (> 48h), so they know
 * whether their RM is acting on the interests they flagged.
 *
 * Gating: renders ONLY for the Teller role, and only when the teller has at
 * least one OWN-submitted referral still in `pending` status aged beyond the
 * 48-hour window (see `stalePendingCount` in ./teller-nudge). Returns `null`
 * (renders nothing) in every other case.
 */
export interface TellerNudgeBannerProps {
  referrals: readonly Referral[];
  /** The signed-in user's display role (e.g. 'Teller') and display name. */
  userRole: string | undefined;
  displayName: string | undefined;
}

export function TellerNudgeBanner({ referrals, userRole, displayName }: TellerNudgeBannerProps) {
  if (userRole !== 'Teller') return null;

  const count = stalePendingCount(referrals, displayName);
  if (count === 0) return null;

  const plural = count === 1 ? 'referral has' : 'referrals have';

  return (
    <div
      role="status"
      className="border-status-reviewed bg-bg-2 text-text-1 rounded-tk-md flex items-start gap-2.5 border border-l-4 px-4 py-3 text-[0.8125rem]"
    >
      <svg
        viewBox="0 0 16 16"
        width="16"
        height="16"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        className="text-status-reviewed mt-0.5 flex-shrink-0"
      >
        <path d="M8 5.5v3.5M8 11.2v.1" />
        <circle cx="8" cy="8" r="6.5" />
      </svg>
      <span>
        <strong className="text-text-0 font-semibold">
          {count} {plural} been pending outreach for more than 48 hours.
        </strong>{' '}
        Your Branch Manager can see this too and can help move things along.
      </span>
    </div>
  );
}
